import { getDbSupabase } from './supabase';
import { downloadWorkbook } from './projectExport';
import { formatYmdAsCrmDate } from '../utils/dateTime';
import ExcelJS from 'exceljs';

export type StorageBillingRow = {
  customer_no: string | null;
  customer_name: string | null;
  salesperson_code: string | null;
  item_no: string | null;
  description: string | null;
  inventory: number | null;
  base_uom: string | null;
  status: string | null;
  day_left: number | null;
  grn_date: string | null;
  grn_quantity: number | null;
  grn_no: string | null;
  is_header: boolean;
  customer_name_full: string | null;
  salesperson_code_full: string | null;
  item_no_full: string | null;
};

export type StorageBillingReport = {
  as_of: string;
  free_months: number;
  row_count: number;
  rows: StorageBillingRow[];
};

export type StorageBillingRowDisplay = {
  customer: string;
  salesperson: string;
  item: string;
  description: string;
  inventory: number | null;
  uom: string;
};

function groupKey(row: StorageBillingRow): string {
  return `${row.customer_no ?? ''}|${row.item_no_full ?? row.item_no ?? ''}`;
}

/** Build item meta from header rows, then attach full customer/item on every line. */
export function enrichStorageBillingRows(
  rows: StorageBillingRow[],
): Array<{ row: StorageBillingRow; display: StorageBillingRowDisplay }> {
  const metaByGroup = new Map<string, { description: string; inventory: number | null; uom: string }>();
  for (const row of rows) {
    if (!row.is_header) continue;
    metaByGroup.set(groupKey(row), {
      description: row.description ?? '',
      inventory: row.inventory,
      uom: row.base_uom ?? '',
    });
  }

  return rows.map((row) => {
    const meta = metaByGroup.get(groupKey(row));
    return {
      row,
      display: {
        customer: row.customer_name_full ?? row.customer_name ?? '',
        salesperson: row.salesperson_code_full ?? row.salesperson_code ?? '',
        item: row.item_no_full ?? row.item_no ?? '',
        description: row.description ?? meta?.description ?? '',
        inventory: row.inventory ?? meta?.inventory ?? null,
        uom: row.base_uom ?? meta?.uom ?? '',
      },
    };
  });
}

function num(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function str(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

function mapRow(raw: Record<string, unknown>): StorageBillingRow {
  return {
    customer_no: str(raw.customer_no),
    customer_name: str(raw.customer_name),
    salesperson_code: str(raw.salesperson_code),
    item_no: str(raw.item_no),
    description: str(raw.description),
    inventory: num(raw.inventory),
    base_uom: str(raw.base_uom),
    status: str(raw.status),
    day_left: num(raw.day_left),
    grn_date: str(raw.grn_date)?.slice(0, 10) ?? null,
    grn_quantity: num(raw.grn_quantity),
    grn_no: str(raw.grn_no),
    is_header: Boolean(raw.is_header),
    customer_name_full: str(raw.customer_name_full),
    salesperson_code_full: str(raw.salesperson_code_full),
    item_no_full: str(raw.item_no_full),
  };
}

export async function fetchStorageBillingReport(options?: {
  asOf?: string;
  freeMonths?: number;
}): Promise<StorageBillingReport> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated in production
  }

  const qs = new URLSearchParams({ report: 'storage_billing' });
  if (options?.asOf?.trim()) qs.set('as_of', options.asOf.trim().slice(0, 10));
  if (options?.freeMonths != null) qs.set('free_months', String(options.freeMonths));

  const response = await fetch(`/report-api?${qs.toString()}`, { headers });
  const payload = (await response.json().catch(() => ({}))) as {
    as_of?: string;
    free_months?: number;
    row_count?: number;
    rows?: Record<string, unknown>[];
    error?: string;
    detail?: string;
    hint?: string;
  };
  if (!response.ok) {
    const parts = [payload.error, payload.detail, payload.hint].filter(Boolean);
    throw new Error(parts.join(' — ') || `Storage billing failed (${response.status})`);
  }

  const rows = Array.isArray(payload.rows) ? payload.rows.map((row) => mapRow(row)) : [];
  return {
    as_of: str(payload.as_of) ?? options?.asOf?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    free_months: num(payload.free_months) ?? options?.freeMonths ?? 3,
    row_count: num(payload.row_count) ?? rows.length,
    rows,
  };
}

function formatDdMmYyyy(iso: string | null | undefined): string {
  if (!iso) return '';
  const raw = iso.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatYmdAsCrmDate(raw);
  return iso;
}

export async function exportStorageBillingExcel(
  report: StorageBillingReport,
  options?: {
    /** Summary sheet rows (defaults to all enriched rows). */
    summaryEntries?: Array<{ row: StorageBillingRow; display: StorageBillingRowDisplay }>;
  },
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'BTH Portal';
  const summary = wb.addWorksheet('Summary');
  const overdue = wb.addWorksheet('Overdue');

  const headerColumns = [
    'Customer Name',
    'Salesperson',
    'Item No.',
    'Description',
    'Inventory',
    'Inventory UOM',
    'Status',
    'Day left',
    'GRN Date',
    'GRN Quantity',
    'Document No.',
  ];

  const allEntries = enrichStorageBillingRows(report.rows);
  const summaryEntries = options?.summaryEntries ?? allEntries;
  const overdueEntries = allEntries.filter(({ row }) => row.status === 'Overdue');

  const paintSheet = (
    ws: ExcelJS.Worksheet,
    entries: Array<{ row: StorageBillingRow; display: StorageBillingRowDisplay }>,
    title: string,
  ) => {
    ws.getCell('A1').value = title;
    ws.getCell('A1').font = { bold: true, size: 14 };
    ws.getCell('A2').value = `As of ${formatDdMmYyyy(report.as_of)} · Free storage ${report.free_months} months`;
    ws.addRow([]);
    const head = ws.addRow(headerColumns);
    head.font = { bold: true };
    head.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8EEF5' },
      };
    });

    for (const { row, display } of entries) {
      ws.addRow([
        display.customer,
        display.salesperson,
        display.item,
        display.description,
        display.inventory,
        display.uom,
        row.status ?? '',
        row.day_left,
        row.grn_date ? formatDdMmYyyy(row.grn_date) : '',
        row.grn_quantity,
        row.grn_no ?? '',
      ]);
    }

    ws.columns = [
      { width: 42 },
      { width: 14 },
      { width: 28 },
      { width: 48 },
      { width: 12 },
      { width: 12 },
      { width: 20 },
      { width: 10 },
      { width: 12 },
      { width: 14 },
      { width: 16 },
    ];
  };

  const summaryTitle =
    summaryEntries.length !== allEntries.length
      ? `Storage Billing — Filtered (${summaryEntries.length.toLocaleString()} rows)`
      : 'Storage Billing — Summary';

  paintSheet(summary, summaryEntries, summaryTitle);
  paintSheet(overdue, overdueEntries, 'Storage Billing — Overdue');

  const stamp = report.as_of.replace(/-/g, '');
  await downloadWorkbook(wb, `Storage Billing Report - as of ${stamp}.xlsx`);
}
